import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MeasurementForm } from '../components/MeasurementForm';
import { ImageUpload } from '../components/ImageUpload';
import { toast } from 'sonner';
import { Footer } from '../components/Footer';
import { authAxios } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderForm() {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [existingClient, setExistingClient] = useState(null);
  const [clientName, setClientName] = useState(searchParams.get('name') || '');
  const [clientPhone, setClientPhone] = useState('');
  const [clientGender, setClientGender] = useState('female');
  const [garmentType, setGarmentType] = useState('upper');
  const [measurements, setMeasurements] = useState({});
  const [specialNotes, setSpecialNotes] = useState('');
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [prevMeasurements, setPrevMeasurements] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);

  useEffect(() => {
    if (clientId) {
      // Fetch client info
      authAxios.get(`${API}/clients/search?q=${clientId}`).then(({ data }) => {
        const found = data.find(c => c.id === clientId);
        if (found) {
          setExistingClient(found);
          setClientName(found.name);
          setClientPhone(found.phone);
          setClientGender(found.gender || 'female');
        }
      }).catch(() => {});

      // Fetch previous measurements
      setLoadingPrev(true);
      authAxios.get(`${API}/clients/${clientId}/latest-measurements`).then(({ data }) => {
        if (data.measurements && Object.keys(data.measurements).length > 0) {
          setPrevMeasurements(data);
        }
      }).catch(() => {}).finally(() => setLoadingPrev(false));
    }
  }, [clientId]);

  const usePreviousMeasurements = () => {
    if (prevMeasurements?.measurements) {
      setMeasurements(prevMeasurements.measurements);
      if (prevMeasurements.garment_type) setGarmentType(prevMeasurements.garment_type);
      toast.success('Previous measurements loaded');
    }
  };

  const handleMeasurementChange = (key, value) => {
    setMeasurements(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientName || !clientPhone) { toast.error('Enter client name and phone'); return; }
    setSubmitting(true);
    try {
      let cId = clientId;
      if (!cId) {
        const { data: clientData } = await authAxios.post(`${API}/clients`, { name: clientName, phone: clientPhone, gender: clientGender });
        cId = clientData.id;
      }
      await authAxios.post(`${API}/orders`, {
        client_id: cId, garment_type: garmentType, measurements, special_notes: specialNotes, reference_image_paths: images
      });
      setSuccess(true);
      toast.success('Order created!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to create order');
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Check className="w-7 h-7 text-green-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-xl font-sans font-bold text-[#1C1C1E]" style={{ fontFamily: "'DM Sans', sans-serif" }}>Order Created!</h2>
          <p className="text-sm text-[#8E8E93] mt-1">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      <header className="bg-white border-b border-[#E5E5EA] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-2.5 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-[#8E8E93] h-8 w-8 p-0" data-testid="order-back-btn">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          <img src="/logo.png" alt="Payal Garg" className="h-7 w-auto hidden sm:block" />
          <span className="font-sans font-bold text-base sm:text-lg text-[#1C1C1E] truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {existingClient ? `Order for ${existingClient.name}` : 'New Client & Order'}
          </span>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 py-5 sm:py-8 flex-1 w-full">
        {/* Client Info */}
        {!existingClient && (
          <Card className="border-[#E5E5EA] bg-white rounded-2xl shadow-sm mb-4 sm:mb-6">
            <CardContent className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-sans font-bold text-[#1C1C1E] mb-3 sm:mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Client Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <Label className="text-xs font-semibold tracking-wide uppercase text-[#8E8E93] mb-1.5 block">Name</Label>
                  <Input data-testid="client-name-input" className="h-11 sm:h-12 border-[#E5E5EA] focus:ring-1 focus:ring-[#D1D1D6] rounded-2xl" placeholder="Client name" value={clientName} onChange={(e) => setClientName(e.target.value)} required />
                </div>
                <div>
                  <Label className="text-xs font-semibold tracking-wide uppercase text-[#8E8E93] mb-1.5 block">Phone</Label>
                  <Input data-testid="client-phone-input" type="tel" className="h-11 sm:h-12 border-[#E5E5EA] focus:ring-1 focus:ring-[#D1D1D6] rounded-2xl" placeholder="Phone number" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} required />
                </div>
                <div>
                  <Label className="text-xs font-semibold tracking-wide uppercase text-[#8E8E93] mb-1.5 block">Gender</Label>
                  <Select value={clientGender} onValueChange={setClientGender}>
                    <SelectTrigger data-testid="client-gender-select" className="h-11 sm:h-12 border-[#E5E5EA] rounded-2xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Previous Measurements Banner */}
        {prevMeasurements && (
          <Card className="bg-white border-[#C27185]/30 rounded-2xl mb-4 sm:mb-6">
            <CardContent className="p-3 sm:p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <History className="w-4 h-4 text-[#C27185] shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1C1C1E]">Previous measurements available</p>
                  <p className="text-xs text-[#8E8E93] truncate">From order #{prevMeasurements.order_number} &middot; {prevMeasurements.garment_type?.replace('_',' ')}</p>
                </div>
              </div>
              <Button type="button" data-testid="use-prev-measurements-btn" className="h-8 px-3 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-2xl text-xs shrink-0" onClick={usePreviousMeasurements}>
                Use These
              </Button>
            </CardContent>
          </Card>
        )}
        {loadingPrev && <p className="text-xs text-[#8E8E93] mb-3 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading previous measurements...</p>}

        {/* Measurements */}
        <Card className="border-[#E5E5EA] bg-white rounded-2xl shadow-sm mb-4 sm:mb-6">
          <CardContent className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-sans font-bold text-[#1C1C1E] mb-3 sm:mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Measurements</h3>
            <MeasurementForm garmentType={garmentType} measurements={measurements} onChange={handleMeasurementChange} onGarmentTypeChange={setGarmentType} />
          </CardContent>
        </Card>

        {/* Reference Images */}
        <Card className="border-[#E5E5EA] bg-white rounded-2xl shadow-sm mb-4 sm:mb-6">
          <CardContent className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-sans font-bold text-[#1C1C1E] mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Reference Images</h3>
            <p className="text-xs text-[#8E8E93] mb-3">Pinterest screenshots, design refs, or photos</p>
            <ImageUpload images={images} onImagesChange={setImages} />
          </CardContent>
        </Card>

        {/* Special Notes */}
        <Card className="border-[#E5E5EA] bg-white rounded-2xl shadow-sm mb-4 sm:mb-6">
          <CardContent className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-sans font-bold text-[#1C1C1E] mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Special Notes</h3>
            <Textarea data-testid="special-notes-input" className="min-h-[80px] border-[#E5E5EA] focus:ring-1 focus:ring-[#D1D1D6] rounded-2xl text-sm" placeholder="Special instructions, fabric details..." value={specialNotes} onChange={(e) => setSpecialNotes(e.target.value)} />
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-3 pb-4">
          <Button type="button" variant="outline" className="flex-1 h-11 sm:h-12 border-[#E5E5EA] text-[#8E8E93] rounded-2xl text-sm" onClick={() => navigate(-1)}>Cancel</Button>
          <Button data-testid="submit-order-btn" type="submit" className="flex-1 h-11 sm:h-12 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-2xl text-sm sm:text-base font-medium" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Order'}
          </Button>
        </div>
      </form>
      <Footer />
    </div>
  );
}
